/** biome-ignore-all lint/a11y/noNoninteractiveTabindex: avoid-breaking */

'use client';

import React, { type FC, useEffect, useState } from 'react';

import cn from 'classnames';

import type { SwitchProps } from './Switch.types';

import styles from './Styles/Default.module.css';

export const Switch: FC<SwitchProps> = React.forwardRef<HTMLInputElement, SwitchProps>(
  ({ checked, onChange, disabled = false, className, label, description }, ref) => {
    const [isChecked, setChecked] = useState(checked);

    useEffect(() => {
      setChecked(checked);
    }, [checked]);

    const handleSwitch = (
      e: React.ChangeEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>,
    ) => {
      setChecked(!isChecked);
      onChange(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e?.key === 'Enter') {
        handleSwitch(e);
      }
    };

    const toggle = (
      <div className={cn(styles.container, className)}>
        <input
          checked={isChecked}
          disabled={disabled}
          onChange={e => handleSwitch(e)}
          ref={ref}
          type="checkbox"
        />
        <span className={styles.switch} tabIndex={0} onKeyDown={handleKeyDown} />
      </div>
    );

    if (!label && !description) {
      return toggle;
    }

    return (
      <label className={styles.wrapper} data-disabled={disabled || undefined}>
        <div className={styles.content}>
          {label && <span className={styles.label}>{label}</span>}
          {description && <span className={styles.description}>{description}</span>}
        </div>
        {toggle}
      </label>
    );
  },
);

Switch.displayName = 'Switch';
